import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import DateFilter from 'shared/modules/dateFilter.js';
import { palette } from 'shared/constants/colorsConstants.js';
import { PANELS, PANELS_TYPES } from 'app/containers/MainDashboard/consts.js';
import useCueData from 'app/containers/MainDashboard/hooks/react-query/useCueData.js';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner.jsx';
import {
  aggregateCostsByDate,
  getFirstDayOfMonth,
  getGroupSettingByAccount,
} from 'app/containers/MainDashboard/utils.js';
import { useMainDashboardContext } from 'app/containers/MainDashboard/contexts/mainDashboardContext.jsx';
import { CustomizedAxisTick } from 'app/containers/MainDashboard/panels/HistoryWithForecast/CustomizedAxisTick.jsx';
import { PANEL_COMPONENT_TYPES } from 'usage/containers/CustomDashboard/constants/customDashboardConstants.js';
import { useUserSettingsContext } from 'users/utils/contexts/UserSettingsContext.jsx';
import { DisplayMetricTypes } from 'usage/constants/costAndUsageConstants.js';
import ForecastHeader from 'app/containers/MainDashboard/panels/HistoryWithForecast/ForecastHeader.jsx';
import useForecastData from 'app/containers/MainDashboard/hooks/react-query/useForecastData.js';
import AreasChart from 'shared/components/dashboardCharts/AreasChart/AreasChart.jsx';
import { useRootStore } from 'app/contexts/RootStoreContext.jsx';
import { DailyChartCustomTooltip } from './DailyChartCustomTooltip.jsx';
import mainStyles from '../../mainDashboardCommon.module.scss';
import { useCurrencyContext } from 'app/contexts/Currency/CurrencyContext.jsx';

const getMaxValue = (data) => {
  const maxValue = data?.reduce((max, currVal) => {
    const currMax = Math.max(currVal.currMonth || 0, currVal.prevMonth || 0, currVal.currExpected || 0);
    return currMax > max ? currMax : max;
  }, 0);
  return maxValue || 1000;
};

const AreasGradient = (
  <defs>
    <linearGradient id="colorPrevMonth" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={palette.blue[400]} stopOpacity={0} />
      <stop offset="100%" stopColor={palette.blue[400]} stopOpacity={0.4} />
    </linearGradient>
    <linearGradient id="colorCurrentMonth" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={palette.blue[500]} stopOpacity={0} />
      <stop offset="100%" stopColor={palette.blue[500]} stopOpacity={0.4} />
    </linearGradient>
    <linearGradient id="colorForecastMonth" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={palette.azure[500]} stopOpacity={0.4} />
      <stop offset="100%" stopColor={palette.azure[500]} stopOpacity={0} />
    </linearGradient>
    <pattern id="patternForecastMonth" patternUnits="userSpaceOnUse" width="16" height="16">
      <rect width="100%" height="100%" fill="url(#colorForecastMonth)" />
      <path d="M 0 16 L 16 0" stroke="white" strokeWidth="0.8" />
    </pattern>
  </defs>
);

const dataKeys = ['prevMonth', 'currMonth', 'currExpected'];

const dataProperties = {
  prevMonth: { name: 'Previous Month', stroke: palette.blue[400], fill: 'url(#colorPrevMonth)' },
  currMonth: { name: 'Current Month', stroke: palette.blue[500], fill: 'url(#colorCurrentMonth)' },
  currExpected: { name: 'Forecast Months', stroke: palette.azure[500], fill: 'url(#patternForecastMonth)' },
};

const HistoryWithForecast = ({
  isFullWidth = false,
  hideSaveDashboard = false,
  forecastMonths,
  currentOverrideCurrency,
  supportsCurrency,
  accountKey,
  divisionId,
  userKey,
}) => {
  const { numStrAbriviaionByDisplayMetric } = useUserSettingsContext();
  const {
    updatePanelLoadingState,
    dateFromLastMonth,
    getDynamicFilters,
    filters,
    forecastEmptyStateText,
    forecastEmptyStateFiltersText,
    getCurrentDay,
    setForecastMonthlyCost,
  } = useMainDashboardContext();

  const { usersStore, appStore } = useRootStore();
  const currencyContext = useCurrencyContext();
  const [forecastNumMonth, setForecastNumMonth] = React.useState(forecastMonths || 1);
  const cueHook = useCueData(!appStore.isChangeAccountLoading);
  const forecastHook = useForecastData(!forecastEmptyStateFiltersText && !appStore.isChangeAccountLoading);
  const mainUser = usersStore?.getMainUser?.();
  const localOverrideAccount = useMemo(
    () => mainUser?.accounts?.find((x) => Number(x.accountKey) === Number(accountKey)),
    [accountKey, mainUser],
  );
  const currentDay = moment(getCurrentDay(accountKey));
  const withOverrideApiKey = {
    rootStore: { usersStore, appStore },
    accountKey,
    divisionId,
    userKey,
  };
  const params = {
    ...PANELS[PANELS_TYPES.HISTORY_DAILY_WITH_FORECAST].params,
    start: getFirstDayOfMonth(dateFromLastMonth(accountKey)),
    end: currentDay.format('YYYY-MM-DD'),
    groupBy: getGroupSettingByAccount(usersStore, !accountKey),
    isPpApplied: appStore.isPpApplied,
    ...getDynamicFilters(),
    withOverrideApiKey,
  };
  const { data, isLoading: isHistoryDataLoading } = cueHook.fetchCueData(params);

  const { data: forecastDataApi, isLoading: isForecastDataLoading } = forecastHook.fetchForecastDataDaily(
    forecastNumMonth,
    filters.cost?.[0]?.value,
    filters.exclude?.map((f) => f.label),
    currentDay.format('YYYY-MM-DD'),
    appStore.isPpApplied,
    withOverrideApiKey,
  );

  const overrideCurrencySymbol = useMemo(() => {
    if (currentOverrideCurrency) {
      return currencyContext?.currenciesMap?.[currentOverrideCurrency]?.symbolNative;
    }
  }, [currentOverrideCurrency, currencyContext?.currenciesMap]);

  const forecastData = useMemo(() => {
    const copyForecastData = cloneDeep(forecastDataApi);

    if (
      forecastDataApi &&
      currencyContext?.active &&
      (currencyContext.currencyInUse?.code !== currencyContext.providerCurrency ||
        (localOverrideAccount && localOverrideAccount.processingCurrency !== localOverrideAccount.providerCurrency))
    ) {
      let exchangeRate = currencyContext?.exchangeRate;
      if (
        localOverrideAccount &&
        currentOverrideCurrency &&
        currencyContext?.currencyInUse?.code !== currentOverrideCurrency
      ) {
        exchangeRate = localOverrideAccount?.exchangeRate;
      }
      copyForecastData.forecast = copyForecastData.forecast?.map((item) => {
        item.currExpected = Number(item.currExpected) * Number(exchangeRate || 1);
        return item;
      });
    }
    return copyForecastData;
  }, [
    forecastDataApi,
    localOverrideAccount,
    currentOverrideCurrency,
    currencyContext?.exchangeRate,
    currencyContext?.active,
    currencyContext?.currencyInUse?.code,
    currencyContext.providerCurrency,
  ]);

  const isLoading = isHistoryDataLoading || isForecastDataLoading;

  const hideForecast = useMemo(() => {
    if (!accountKey) {
      return !usersStore.isCurrentAccountSingle && !appStore.isKeyCloakManagement;
    }
    return !usersStore.isAccountSingle(accountKey) && !appStore.isKeyCloakManagement;
  }, [accountKey, appStore.isKeyCloakManagement, usersStore]);

  const chartData = useMemo(() => {
    if (!data) return [];
    //calculate prev month data
    const previousMonth = data.filter((d) => d.usage_date < getFirstDayOfMonth(DateFilter.getDate()));
    const prevData = aggregateCostsByDate(previousMonth, 'prevMonth');

    //marge the last day in previous month with first day in current month
    if (prevData.length > 0) {
      prevData[prevData.length - 1].currMonth = prevData[prevData.length - 1].prevMonth;
    }
    //calculate current month data
    const currentMonth = data.filter((d) => d.usage_date >= getFirstDayOfMonth(DateFilter.getDate()));
    const currentData = aggregateCostsByDate(currentMonth, 'currMonth');

    let forecastList = [];
    //merge the lastDay in currentMonth data with firstDate in forecastData
    if (
      forecastData?.forecast?.length &&
      currentData?.length &&
      !forecastEmptyStateFiltersText &&
      moment(forecastData?.forecast[0].fullDate).isSame(moment(currentData[currentData.length - 1].fullDate), 'day')
    ) {
      const currentDay = currentData[currentData.length - 1];
      if (forecastData?.forecast[0].currExpected) {
        currentDay.actualCost = currentDay.currMonth;
        currentDay.currMonth = forecastData?.forecast[0].currExpected;
        currentDay.currExpected = forecastData?.forecast[0].currExpected;
      } else {
        currentDay.currExpected = currentDay.currMonth;
      }
      forecastList = forecastData?.forecast.slice(1);
    } else if (forecastData?.forecast?.length && !forecastEmptyStateFiltersText) {
      forecastList = forecastData?.forecast;

      //marge first day in forecast with last day in current month when not get forecast to current day
      if (!forecastList[0].currMonth) {
        forecastList[0].currMonth = forecastList[0].currExpected;
      }
    }

    const combinedData = [...prevData, ...currentData, ...forecastList];

    // Add accumulated field for each month
    const accumulatedByMonth = {};
    combinedData.forEach((item) => {
      const month = moment(item.fullDate).format('YYYY-MM');
      if (!accumulatedByMonth[month]) {
        accumulatedByMonth[month] = 0;
      }
      accumulatedByMonth[month] += +item.prevMonth || +item.currMonth || +item.currExpected;
      item.accumulated = accumulatedByMonth[month];
    });

    return combinedData;
  }, [data, forecastData, forecastEmptyStateFiltersText]);

  useEffect(() => {
    updatePanelLoadingState(PANELS_TYPES.HISTORY_DAILY_WITH_FORECAST, isLoading);
  }, [isLoading, updatePanelLoadingState]);

  useEffect(() => {
    if (!chartData || isLoading || forecastEmptyStateFiltersText) return;
    const lastDayOfMonth = currentDay.endOf('month').format('YYYY-MM-DD');

    const lastDayItem = chartData.find((item) => moment(item.fullDate).format('YYYY-MM-DD') === lastDayOfMonth);
    setForecastMonthlyCost(lastDayItem?.accumulated);
  }, [chartData, currentDay, setForecastMonthlyCost, isLoading, forecastEmptyStateFiltersText]);
  const customTicks = (props) => <CustomizedAxisTick {...props} data={chartData} />;

  const maxValue = getMaxValue(chartData) * 1.1;

  return (
    <div
      className={classNames(
        mainStyles.panelWrapper,
        !isFullWidth ? mainStyles.halfWidthPanelWrapper : mainStyles.fullWidth,
      )}
    >
      {currencyContext?.isLoading || isLoading ? (
        <Spinner className={mainStyles.spinner} size={SPINNER_SIZES.MEDIUM} />
      ) : (
        <>
          <ForecastHeader
            title={PANELS[PANELS_TYPES.HISTORY_DAILY_WITH_FORECAST].title}
            params={params}
            forecastMonths={[
              { value: 1, label: '1M' },
              { value: 3, label: '3M' },
            ]}
            panelType={PANEL_COMPONENT_TYPES.CURR_VS_PREVIOUS_MONTH_COST}
            forecastNumMonth={forecastNumMonth}
            setForecastNumMonth={setForecastNumMonth}
            hideSaveDashboard={hideSaveDashboard}
            hideForecastDetails={hideForecast}
          />
          <AreasChart
            data={chartData}
            height={300}
            dataKeys={
              hideForecast || forecastEmptyStateText || forecastEmptyStateFiltersText
                ? dataKeys.filter((k) => k !== 'currExpected')
                : dataKeys
            }
            specialGradient={AreasGradient}
            dataProperties={dataProperties}
            specialLegends={['currExpected']}
            tooltipComponent={
              <DailyChartCustomTooltip currentOverrideCurrency={supportsCurrency ? currentOverrideCurrency : null} />
            }
            axisSettings={{
              xCustomTick: customTicks,
              yTickFormatter: (value) =>
                numStrAbriviaionByDisplayMetric(
                  maxValue,
                  value,
                  DisplayMetricTypes.COST,
                  supportsCurrency ? overrideCurrencySymbol : null,
                ),
              maxValue: maxValue,
            }}
            info={hideForecast ? null : forecastEmptyStateText || forecastEmptyStateFiltersText}
          />
        </>
      )}
    </div>
  );
};

export default HistoryWithForecast;
